import type { CloudfrontLocation } from '@portal/state/hooks/useCloudfrontLocation';
import { GlobalBanner } from '@portal/types/studio/banner';
import { PageResult } from '@portal/types/studio/page';
import { PromoBanner } from '@portal/types/studio/studio';
import type { NextRouter } from 'next/router';
import { shouldShowForCountry } from './shouldShowForCountry';

interface SanityReference {
  _ref: string;
  _type: 'reference';
}

interface PathObject {
  _type: 'pathObject';
  path?: string;
}

type PageEntry = SanityReference | PageResult | PathObject;

export const shouldShowBanner = (
  bannerData: GlobalBanner[] | PromoBanner[],
  currentPath: string
): GlobalBanner | PromoBanner | undefined => {
  if (!bannerData?.length) return undefined;

  // Strip out query parameters from the path
  const cleanPath = currentPath.split('?')[0];

  // Find the first active banner
  const activeBanner = bannerData.find((banner) => {
    const { includePages, excludePages } = banner;

    // Helper function to check if path matches any page in array
    const pathMatchesPage = (pages: PageEntry[] | undefined) => {
      if (!pages) return false;

      return pages.some((page) => {
        // Skip null/undefined entries
        if (!page) return false;

        // Handle reference type
        if (typeof page === 'object' && '_ref' in page) {
          // eslint-disable-next-line no-underscore-dangle
          return cleanPath.includes(page._ref);
        }
        // Handle Sanity slug object structure
        if (typeof page === 'object' && 'slug' in page) {
          return cleanPath === page.slug.current;
        }
        // Handle path object type
        if (typeof page === 'object' && 'path' in page) {
          return cleanPath === page.path;
        }
        return false;
      });
    };

    // If both toggles are false, show everywhere
    if (!banner.include && !banner.exclude) {
      return true;
    }

    // If only exclude is on, show everywhere except excluded pages
    if (!banner.include && banner.exclude) {
      return !pathMatchesPage(excludePages);
    }

    // If only include is on, show only on included pages
    if (banner.include && !banner.exclude) {
      return pathMatchesPage(includePages);
    }

    // If both are on (shouldn't happen but handling anyway)
    if (banner.include && banner.exclude) {
      return pathMatchesPage(includePages) && !pathMatchesPage(excludePages);
    }

    return false;
  });

  return activeBanner;
};

export const getActiveBannerForHeader = ({
  bannerData = [],
  country,
  path
}: {
  bannerData: GlobalBanner[] | undefined;
  country: CloudfrontLocation['country'] | undefined;
  path: NextRouter['asPath'];
}) => {
  const banner = shouldShowBanner(bannerData, path) as GlobalBanner | undefined;
  if (!banner) {
    return null;
  }

  return shouldShowForCountry(banner, country) ? banner : null;
};

export const getActivePromoBannerForHeader = ({
  promoBannerData = [],
  path
}: {
  promoBannerData: PromoBanner[] | undefined;
  path: NextRouter['asPath'];
}) => shouldShowBanner(promoBannerData, path) as PromoBanner | undefined;
